import { useCallback, useEffect, useRef } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

type ExecuteRecaptcha = (action?: string) => Promise<string>;

const RECAPTCHA_READY_TIMEOUT_MS = 10000;

export const useRecaptcha = (action: string) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const executeRecaptchaRef = useRef(executeRecaptcha);
  const pendingResolversRef = useRef<((execute: ExecuteRecaptcha) => void)[]>(
    [],
  );

  useEffect(() => {
    executeRecaptchaRef.current = executeRecaptcha;

    if (!executeRecaptcha) {
      return;
    }

    pendingResolversRef.current.forEach((resolve) => resolve(executeRecaptcha));
    pendingResolversRef.current = [];
  }, [executeRecaptcha]);

  const waitForExecuteRecaptcha = useCallback(() => {
    if (executeRecaptchaRef.current) {
      return Promise.resolve(executeRecaptchaRef.current);
    }

    return new Promise<ExecuteRecaptcha | undefined>((resolve) => {
      const timeout = setTimeout(() => {
        pendingResolversRef.current = pendingResolversRef.current.filter(
          (pending) => pending !== onReady,
        );
        resolve(undefined);
      }, RECAPTCHA_READY_TIMEOUT_MS);

      const onReady = (execute: ExecuteRecaptcha) => {
        clearTimeout(timeout);
        resolve(execute);
      };

      pendingResolversRef.current.push(onReady);
    });
  }, []);

  const handleReCaptchaVerify = useCallback(async () => {
    const execute = await waitForExecuteRecaptcha();

    if (!execute) {
      return;
    }

    return execute(action);
  }, [action, waitForExecuteRecaptcha]);

  return { handleReCaptchaVerify };
};
